// The player's collection (ADR 0017): what they added from the store. The server is the record; this browser keeps a
// read cache (emptied at sign-out) so the pickers work during a network drop. Official entries are known by id (their
// content ships with the client); a published creation comes with its current version and joins the catalogue.
import type { CollectionItem, Creation, CreationKind, WalletResponse } from '@gruvetto/contracts';
import { isPublishedId, type AnyEntry, type Entry } from '@gruvetto/creations';
import { api, isLoggedIn } from './api.js';
import { db } from './db.js';
import { state, update } from './store.js';
import { catalog, catalogReady, legacyInstalled } from './creations.js';

const ids = new Set<string>();
const listeners = new Set<() => void>();
const changed = () => { for (const fn of listeners) fn(); };
export const onCollectionChange = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };

/** The catalogue entry of a published creation of the collection. */
const entryOf = (c: Creation): AnyEntry => ({
  id: c.id, version: c.version, source: 'library', creation: { kind: c.kind, name: c.name, desc: c.desc, tags: c.tags, content: c.content },
  author: c.author, remixOf: c.remixOf, remote: { id: c.id, version: c.version }, provenance: c.provenance, updatedAt: c.updatedAt,
} as AnyEntry);
/** Replaces the collection with the server's (or the cache's) list. */
function apply(items: CollectionItem[]) {
  ids.clear();
  catalog.clear('library');
  const entries: AnyEntry[] = [];
  for (const it of items) { ids.add(it.id); if (it.creation) entries.push(entryOf(it.creation)); }
  if (entries.length) catalog.put(...entries);
  changed();
}

/** Reads the cache (start-up, offline). */
export const collectionReady: Promise<void> = (async () => {
  await catalogReady;
  apply(await db.all<CollectionItem>('collection'));
})().catch(e => console.warn('Collection cache unavailable', e));

/** Pulls the collection from the account and refreshes the cache. */
export async function pullCollection(): Promise<void> {
  await collectionReady;
  const items = await api<CollectionItem[]>('/me/collection');
  apply(items);
  await db.clear('collection');
  for (const it of items) await db.put('collection', it);
  await migrateLegacy();
}
/** Library copies installed on this device before ADR 0017 join the account's collection once, then leave the device. */
async function migrateLegacy() {
  if (!legacyInstalled.length) return;
  for (const e of legacyInstalled) {
    const id = e.remote?.id ?? e.id;
    try { if (!ids.has(id)) await add(id); } catch { /* deleted or hidden since: gone */ }
    await db.del('creations', e.id);
  }
  legacyInstalled.length = 0;
}
/** Sign-out: the collection leaves this browser (the IndexedDB store is emptied by data/sync.ts with the others). */
export function clearCollection() { apply([]); }

// ---------- Reading ----------
export const inCollection = (id: string) => ids.has(id);
/** Whether a catalogue entry is the player's: a draft of theirs, or something they added. */
export const isMine = (e: AnyEntry) => e.source === 'local' || e.source === 'library' || ids.has(e.id);
/** The player's entries of a type: what every picker shows (official starter set, additions from the store, drafts). */
export const mine = <K extends CreationKind>(kind: K): Entry<K>[] => catalog.list(kind).filter(isMine);
export const collectionSize = () => ids.size;

// ---------- Changing ----------
/** Adds a store item (official id or published creation). A published creation joins the catalogue with its content. */
export async function add(id: string): Promise<CollectionItem> {
  const it = await api<CollectionItem>('/me/collection', { method: 'POST', body: { id } });
  ids.add(it.id);
  if (it.creation) catalog.put(entryOf(it.creation));
  await db.put('collection', it);
  changed();
  return it;
}
export async function remove(id: string): Promise<void> {
  await api(`/me/collection/${encodeURIComponent(id)}`, { method: 'DELETE' });
  ids.delete(id);
  if (isPublishedId(id)) catalog.remove(id);
  await db.del('collection', id);
  changed();
}
/** A published creation the player wants to play right away: added if it is not theirs yet, then its entry. */
export async function ensure(c: Creation): Promise<AnyEntry> {
  return catalog.get(c.id) ?? (await add(c.id), catalog.get(c.id)!);
}

// ---------- Coins ----------
export const wallet = () => api<WalletResponse>('/me/wallet');
export const coins = () => state.progress.coins || 0;
/** Buys a sold outfit: the server debits the coins and adds it to the collection in one transaction. */
export async function buy(id: string): Promise<{ coins: number; owned: string }> {
  const r = await api<{ coins: number; owned: string }>(`/shop/${encodeURIComponent(id)}/buy`, { method: 'POST' });
  update(s => { s.progress.coins = r.coins; });
  if (isLoggedIn()) await add(id).catch(() => {}); // the row exists already: this fetches the content into the catalogue
  return r;
}
